import React, { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Modal } from "react-bootstrap";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import { showError, showSuccess } from "toast/toast";
import { getAuthCookie } from "../../utils/authCookies";

type JobTitleRow = {
  id: string;
  title: string;
  description: string;
  status: boolean | string;
  created_at?: string;
};

function fuzzyTextFilter<TData>(row: Row<TData>, columnId: string, filterValue: string) {
  const raw = row.getValue(columnId);
  return String(raw || "")
    .toLowerCase()
    .includes(String(filterValue || "").toLowerCase());
}

const columnHelper = createColumnHelper<JobTitleRow>();

const statusPill = (status?: boolean | string) => {
  const value =
    typeof status === "boolean" ? (status ? "active" : "inactive") : String(status || "").toLowerCase();

  return (
    <span
      className={`badge rounded-pill px-3 py-2 ${
        value === "active"
          ? "bg-success-subtle text-success-emphasis"
          : "bg-secondary-subtle text-secondary-emphasis"
      }`}
    >
      {typeof status === "boolean" ? (status ? "Active" : "Inactive") : status || "--"}
    </span>
  );
};

const JobTitles: React.FC = () => {
  const topRef = useRef<HTMLDivElement>(null);
  const token = typeof window !== "undefined" ? getAuthCookie("sessionToken") : null;

  const [rows, setRows] = useState<JobTitleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [selectedJobTitleId, setSelectedJobTitleId] = useState("");
  const [deletingId, setDeletingId] = useState("");

  const loadJobTitles = async () => {
    if (!token) {
      setRows([]);
      setRowCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/job-titles/datatable`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          search: globalFilter,
          filters: {},
          sort: {
            field: "title",
            order: "asc",
          },
          fields: ["id", "title", "description", "status", "created_at"],
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.message || result?.error || "Failed to fetch job titles");
      }

      setRows(Array.isArray(result?.data) ? result.data : []);
      setRowCount(Number(result?.total) || 0);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to fetch job titles");
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      status: "true",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setSubmitting(true);

      try {
        if (!token) {
          throw new Error("Session expired. Please login again.");
        }

        const isEditMode = modalMode === "edit" && selectedJobTitleId;
        const response = await fetch(
          isEditMode
            ? `${process.env.REACT_APP_API_URL}/job-titles/${selectedJobTitleId}`
            : `${process.env.REACT_APP_API_URL}/job-titles`,
          {
            method: isEditMode ? "PUT" : "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              title: values.title,
              description: values.description,
              status: values.status === "true",
              is_global: true,
            }),
          }
        );

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result?.message || result?.error || `Failed to ${isEditMode ? "update" : "create"} job title`);
        }

        showSuccess(`Job Title ${isEditMode ? "updated" : "added"} successfully!`);
        setShowModal(false);
        setModalMode("add");
        setSelectedJobTitleId("");
        resetForm({
          values: {
            title: "",
            description: "",
            status: "true",
          },
        });
        await loadJobTitles();
      } catch (error) {
        showError(error instanceof Error ? error.message : `Failed to ${modalMode === "edit" ? "update" : "create"} job title`);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleOpenAddModal = () => {
    setModalMode("add");
    setSelectedJobTitleId("");
    formik.resetForm({
      values: {
        title: "",
        description: "",
        status: "true",
      },
    });
    setShowModal(true);
  };

  const handleEditClick = async (jobTitleId: string) => {
    if (!token) {
      showError("Session expired. Please login again.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/job-titles/${jobTitleId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.message || result?.error || "Failed to fetch job title");
      }

      setModalMode("edit");
      setSelectedJobTitleId(String(result?.id || jobTitleId));
      formik.setValues({
        title: String(result?.title || ""),
        description: String(result?.description || ""),
        status: result?.status === false ? "false" : "true",
      });
      setShowModal(true);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to fetch job title");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = async (jobTitleId: string) => {
    if (!token) {
      showError("Session expired. Please login again.");
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this job title?");
    if (!confirmed) return;

    setDeletingId(jobTitleId);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/job-titles/${jobTitleId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.message || result?.error || "Failed to delete job title");
      }

      showSuccess(result?.message || "Job title deleted successfully");
      await loadJobTitles();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to delete job title");
    } finally {
      setDeletingId("");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalMode("add");
    setSelectedJobTitleId("");
    formik.resetForm({
      values: {
        title: "",
        description: "",
        status: "true",
      },
    });
  };

  useEffect(() => {
    void loadJobTitles();
  }, [token, pagination.pageIndex, pagination.pageSize, globalFilter]);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),
      columnHelper.accessor("title", {
        id: "title",
        header: () => <div className="th-text">Title</div>,
        cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
        filterFn: fuzzyTextFilter,
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: () => <div className="th-text">Description</div>,
        cell: (info) => (
          <div className="text-sm text-truncate" style={{ maxWidth: 320 }}>
            {info.getValue() || "--"}
          </div>
        ),
        filterFn: fuzzyTextFilter,
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: () => <div className="th-text">Status</div>,
        cell: (info) => <div className="text-sm">{statusPill(info.getValue())}</div>,
        filterFn: fuzzyTextFilter,
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => (
          <div className="d-flex align-items-center gap-2">
            <EditIconButton
              className="icon-square"
              title="Edit"
              onClick={() => void handleEditClick(String(info.row.original.id || ""))}
            />
            <DeleteIconButton
              title="Delete"
              className="icon-square danger"
              onClick={() => void handleDeleteClick(String(info.row.original.id || ""))}
              disabled={deletingId === String(info.row.original.id || "")}
            />
          </div>
        ),
      }),
    ],
    [pagination, deletingId]
  );

  const table = useReactTable({
    data: rows,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: { globalFilter, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
  });

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [pagination.pageIndex]);

  return (
    <div className="content" ref={topRef}>
      <style>
        {`
          .job-title-modal .job-title-form-select {
            width: 20% !important;
            display: block !important;
            min-height: 42px;
          }
        `}
      </style>

      <div className="po-header mb-4 shadow-border-datatables">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2 d-flex align-items-center">
            <h4 className="po-title mb-0">Job Titles</h4>
          </div>

          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            <div className="input-group po-search">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by title, description, or status..."
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setGlobalFilter(searchInput);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }
                }}
              />
            </div>
            <button
              type="button"
              onClick={handleOpenAddModal}
              className="btn btn-primary table-addbutton"
            >
              + Add JobTitle
            </button>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0">Job Titles</h5>
          <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            {rowCount} records
          </span>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />

        <div className="rdt-desktop">
          <div className="production-scroll" style={{ "--rdt-min": "1000px" } as React.CSSProperties}>
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="id"
            titleColId="title"
            actionColId="actions"
          />
        </div>
      </div>

      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          {loading ? "Loading..." : "No records found"}
        </div>
      ) : (
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      )}

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg" dialogClassName="job-title-modal">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="fw-bold">
            {modalMode === "edit" ? "Edit Job Title" : "Add Job Title"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <form onSubmit={formik.handleSubmit}>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label fw-semibold">
                  Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  className={`form-control ${formik.touched.title && formik.errors.title ? "is-invalid" : ""}`}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter job title"
                />
                {formik.touched.title && formik.errors.title ? (
                  <div className="invalid-feedback">{formik.errors.title}</div>
                ) : null}
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">Status</label>
                <select
                  name="status"
                  className="form-select job-title-form-select"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">Description</label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter description"
                  rows={4}
                />
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <button
            type="button"
            className="btn back-btn surface-btn fw-bold fs-16 mt-3"
            onClick={handleCloseModal}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary save-btn surface-btn fw-bold fs-16 mt-3"
            onClick={() => formik.handleSubmit()}
            disabled={submitting}
          >
            {submitting ? "Saving..." : modalMode === "edit" ? "Update" : "Add Job Title"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default JobTitles;
